import React, {useEffect, useRef, useState} from 'react';

import Draggable from 'react-draggable';
import InputTitleComponent from './redesign/inputComponentV2/components/inputTitleComponent/inputTitleComponent';
import ToolTipComponent from './toolTipComponent';

import './sliderComponent.css';

const SLIDER_DOT_WIDTH = 14; /* align with et-v2-slider-dot width */

const SliderComponent = ({
  id = '',
  hideDot = false,
  isDisabled = false,
  noWaitOnSlide = false,
  showPercentage = true,
  showPercentageSymbol = true,
  showToolTip = true,
  defaultPercentage = 0,
  titleTxt = null,
  toolTipPosition = 'right',
  toolTipTxt = null,
  minValue = 0,
  maxValue = 100,
  onPercentageChange = () => {},
}) => {
  const draggableNodeRef = useRef();
  const sliderRef = useRef(null);
  const [dotPosition, setDotPosition] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [gradientColor, setGradientColor] = useState(
    `linear-gradient(to right, #2961FA 0px, #2D27561A 0px)`,
  );
  const [isSliding, setIsSliding] = useState(false);
  const [showToolTipComponent, setShowToolTipComponent] = useState(false);
  const [slidingTimeoutId, setSlidingTimeoutId] = useState(null);

  useEffect(() => {
    if (defaultPercentage !== percentage) {
      doWhenDefaultPercentageChanges();
    }
    return () => {
      if (slidingTimeoutId) clearTimeout(slidingTimeoutId);
    };
  }, [defaultPercentage]);

  const onDragStart = () => {
    setIsSliding(true);
  };

  const onDrag = (e, {x: dotPosition}) => {
    const sliderBarContainerWidth = sliderRef?.current?.offsetWidth ?? 0;
    let percentage = (
      (dotPosition / (sliderBarContainerWidth - SLIDER_DOT_WIDTH)) *
      maxValue
    ).toFixed(2);
    if (percentage >= minValue && percentage <= maxValue) {
      const gradientColor = `linear-gradient(to right, #2961FA ${dotPosition}px, #2D27561A ${dotPosition}px)`;
      setDotPosition(dotPosition);
      setPercentage(percentage);
      setGradientColor(gradientColor);

      const timeoutMils = noWaitOnSlide ? 0 : 1500;
      if (slidingTimeoutId) clearTimeout(slidingTimeoutId);
      const timeoutId = setTimeout(() => {
        onPercentageChange(id, percentage);
      }, timeoutMils);
      setSlidingTimeoutId(timeoutId);
    }
  };

  const onDragStop = () => {
    setIsSliding(false);
  };

  const handleToolTip = () => {
    if (showToolTip) {
      setShowToolTipComponent(!showToolTipComponent);
    }
  };

  const doWhenDefaultPercentageChanges = () => {
    const sliderBarContainerWidth = sliderRef?.current?.offsetWidth ?? 0;
    const percentage =
      defaultPercentage >= 0 &&
      defaultPercentage >= minValue &&
      defaultPercentage <= maxValue
        ? defaultPercentage
        : minValue;
    const sliderDotWidth = hideDot ? 0 : SLIDER_DOT_WIDTH;
    const dotPosition =
      (percentage / maxValue) * (sliderBarContainerWidth - sliderDotWidth);
    const gradientColor = `linear-gradient(to right, #2961FA ${dotPosition}px, #2D27561A ${dotPosition}px)`;
    setPercentage(percentage);
    setDotPosition(dotPosition);
    setGradientColor(gradientColor);
  };

  return (
    <>
      <InputTitleComponent
        titleTxt={titleTxt}
        toolTipTxt={toolTipTxt}
        toolTipPosition={toolTipPosition}
      />
      <div
        className={`et-v2-slider-container ${isDisabled ? 'et-v2-slider-container-disabled' : ''}`}>
        <div
          ref={sliderRef}
          className="et-v2-slider-bar"
          style={{background: gradientColor}}
          onMouseEnter={handleToolTip}
          onMouseLeave={handleToolTip}
          onClick={handleToolTip}>
          {!hideDot && (
            <Draggable
              nodeRef={draggableNodeRef}
              disabled={isDisabled}
              bounds="parent"
              position={{x: dotPosition, y: 0}}
              onStart={onDragStart}
              onDrag={onDrag}
              onStop={onDragStop}>
              <div
                ref={draggableNodeRef}
                className={`et-v2-slider-dot ${isSliding ? 'et-v2-slider-dot-sliding' : ''} ${!showPercentage ? 'et-v2-slider-dot-with-no-percentage' : ''}`}>
                {showToolTipComponent && (
                  <ToolTipComponent
                    toolTipPosition="top"
                    toolTipTxt={percentage}
                  />
                )}
              </div>
            </Draggable>
          )}
        </div>
        {showPercentage && (
          <div className="et-v2-slider-percentage-txt">{`${percentage}${showPercentageSymbol ? '%' : ''}`}</div>
        )}
      </div>
    </>
  );
};

export default SliderComponent;
